import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvents } from '../../context/EventsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Edit, Trash2, Check, CreditCard, Building
} from 'lucide-react';

const PaymentMethodsAdmin = () => {
  const navigate = useNavigate();
  const { getPaymentMethods, createPaymentMethod, updatePaymentMethod, deletePaymentMethod } = useEvents();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPm, setEditingPm] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', active: true,
    requiresProof: false, requiresManualConfirmation: false, bankInfo: null
  });
  const [showBankFields, setShowBankFields] = useState(false);
  const [bankForm, setBankForm] = useState({ banco: '', cuenta: '', clabe: '', titular: '' });

  useEffect(() => {
    const load = async () => {
      const pms = await getPaymentMethods();
      setPaymentMethods(pms || []);
    };
    load();
  }, [getPaymentMethods]);

  const openNew = () => {
    setEditingPm(null);
    setForm({ name: '', description: '', active: true, requiresProof: false, requiresManualConfirmation: false, bankInfo: null });
    setShowBankFields(false);
    setBankForm({ banco: '', cuenta: '', clabe: '', titular: '' });
    setShowDialog(true);
  };

  const openEdit = (pm) => {
    setEditingPm(pm);
    setForm({
      name: pm.name,
      description: pm.description || '',
      active: pm.active,
      requiresProof: pm.requiresProof,
      requiresManualConfirmation: pm.requiresManualConfirmation || false,
      bankInfo: pm.bankInfo
    });
    setShowBankFields(!!pm.bankInfo);
    setBankForm(pm.bankInfo || { banco: '', cuenta: '', clabe: '', titular: '' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    const data = {
      name: form.name,
      description: form.description,
      active: form.active,
      requires_proof: showBankFields,
      requires_manual_confirmation: form.requiresManualConfirmation,
      bank_info: showBankFields && bankForm.banco ? bankForm : null,
    };
    if (editingPm) {
      await updatePaymentMethod(editingPm.id, data);
    } else {
      await createPaymentMethod(data);
    }
    const pms = await getPaymentMethods();
    setPaymentMethods(pms || []);
    setShowDialog(false);
  };

  const handleDelete = async (pmId) => {
    await deletePaymentMethod(pmId);
    setPaymentMethods(prev => prev.filter(pm => pm.id !== pmId));
  };

  return (
    <div data-testid="payment-methods-admin" className="min-h-screen bg-[#050505]">
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
            </Button>
            <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">Métodos de Pago</h1>
          </div>
          <Button onClick={openNew} className="bg-primary hover:bg-accent shrink-0">
            <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Nuevo Método</span><span className="sm:hidden">Nuevo</span>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="space-y-3">
          {paymentMethods.map(pm => (
            <div key={pm.id} className="bg-secondary border border-border rounded-lg p-3 sm:p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${pm.requiresProof ? 'bg-blue-500/10' : 'bg-emerald-500/10'}`}>
                  {pm.requiresProof
                    ? <Building className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                    : <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                  }
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium text-foreground">{pm.name}</p>
                    <Badge className={pm.active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-[10px]'}>
                      {pm.active ? 'Activo' : 'Inactivo'}
                    </Badge>
                    {pm.requiresManualConfirmation && (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Manual</Badge>
                    )}
                  </div>
                  {pm.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{pm.description}</p>}
                  {pm.bankInfo && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{pm.bankInfo.banco} · {pm.bankInfo.cuenta}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(pm)} className="text-muted-foreground hover:text-foreground">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(pm.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {paymentMethods.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No hay métodos de pago configurados
            </div>
          )}
        </div>
      </main>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-[#0a0a0a] border-border">
          <DialogHeader>
            <DialogTitle>{editingPm ? 'Editar' : 'Nuevo'} Método de Pago</DialogTitle>
            <DialogDescription>Configura el método de pago</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Transferencia BAC, Cubo Pago..." className="bg-background border-border" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Descripción (visible al cliente)</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Mensaje que verá el cliente..." className="bg-background border-border min-h-[60px] resize-none" />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="accent-primary" />
                <span className="text-sm text-foreground">Activo</span>
              </label>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showBankFields} onChange={e => setShowBankFields(e.target.checked)} className="accent-primary" />
                <span className="text-sm text-foreground">Requiere comprobante + datos bancarios</span>
              </label>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.requiresManualConfirmation} onChange={e => setForm({ ...form, requiresManualConfirmation: e.target.checked })} className="accent-primary" />
                <span className="text-sm text-foreground">Requiere confirmación manual del admin</span>
              </label>
            </div>
            {form.requiresManualConfirmation && (
              <p className="text-xs text-amber-400/80 pl-6">La reserva quedará pendiente hasta que el admin la apruebe o rechace.</p>
            )}
            {showBankFields && (
              <div className="space-y-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <h4 className="text-xs font-medium text-foreground">Datos Bancarios</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Banco</Label>
                    <Input value={bankForm.banco} onChange={e => setBankForm({ ...bankForm, banco: e.target.value })} className="bg-background border-border h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Cuenta</Label>
                    <Input value={bankForm.cuenta} onChange={e => setBankForm({ ...bankForm, cuenta: e.target.value })} className="bg-background border-border h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">CLABE</Label>
                    <Input value={bankForm.clabe} onChange={e => setBankForm({ ...bankForm, clabe: e.target.value })} className="bg-background border-border h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Titular</Label>
                    <Input value={bankForm.titular} onChange={e => setBankForm({ ...bankForm, titular: e.target.value })} className="bg-background border-border h-8 text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-border">Cancelar</Button>
            <Button onClick={handleSave} className="bg-primary hover:bg-accent" disabled={!form.name.trim()}>
              <Check className="w-4 h-4 mr-2" />{editingPm ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodsAdmin;
